import React from "react";
import {
  Box,
  Button,
  IconButton,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";

export default function ReportActions({
  item,
  isMobile,
  evidencePackStatus = "idle",
  downloading = false,
  evidencePackDownloading = false,
  onDownloadPdf,
  onRequestEvidencePack,
  onDownloadEvidencePack,
}) {
  const reportType = (item?.report_type || "").toLowerCase();
  const isRiskReport = reportType !== "flash_report";

  const isEvidenceBusy =
    evidencePackStatus === "processing" || evidencePackDownloading;

  if (isMobile) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
        {isRiskReport && (
          <>
            {evidencePackStatus === "idle" && (
              <Tooltip title="Request evidence pack" arrow>
                <IconButton
                  size="small"
                  onClick={onRequestEvidencePack}
                  aria-label="Request evidence pack"
                >
                  <Inventory2OutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {evidencePackStatus === "processing" && (
              <Tooltip title="Processing evidence pack" arrow>
                <Box
                  component="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 34,
                    height: 34,
                  }}
                >
                  <CircularProgress size={18} />
                </Box>
              </Tooltip>
            )}

            {evidencePackStatus === "completed" && (
              <Tooltip title="Download evidence pack" arrow>
                <IconButton
                  size="small"
                  onClick={onDownloadEvidencePack}
                  disabled={evidencePackDownloading}
                  aria-label="Download evidence pack"
                >
                  {evidencePackDownloading ? (
                    <CircularProgress size={18} />
                  ) : (
                    <Inventory2OutlinedIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>
            )}

            {evidencePackStatus === "failed" && (
              <Tooltip title="Retry evidence pack" arrow>
                <IconButton
                  size="small"
                  onClick={onRequestEvidencePack}
                  aria-label="Retry evidence pack"
                  sx={{ color: "#B42318" }}
                >
                  <Inventory2OutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}

        <Tooltip title="Download PDF" arrow>
          <IconButton
            size="small"
            onClick={onDownloadPdf}
            disabled={downloading}
            aria-label="Download PDF"
          >
            {downloading ? (
              <CircularProgress size={18} />
            ) : (
              <DownloadIcon fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>
    );
  }

  const outlineSx = {
    textTransform: "none",
    fontSize: "14px",
    fontWeight: 500,
    height: 38,
    px: 1.75,
    borderColor: "rgba(0,0,0,0.23)",
    color: "text.primary",
    "&:hover": { borderColor: "rgba(0,0,0,0.5)" },
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {isRiskReport && (
        <>
          {evidencePackStatus === "idle" && (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<Inventory2OutlinedIcon />}
              onClick={onRequestEvidencePack}
              sx={outlineSx}
            >
              Request evidence pack
            </Button>
          )}

          {evidencePackStatus === "processing" && (
            <Button
              variant="outlined"
              size="medium"
              disabled
              startIcon={<CircularProgress size={16} />}
              sx={{
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                height: 38,
                px: 1.75,
              }}
            >
              Processing...
            </Button>
          )}

          {evidencePackStatus === "completed" && (
            <Button
              variant="outlined"
              size="medium"
              startIcon={
                evidencePackDownloading ? (
                  <CircularProgress size={16} />
                ) : (
                  <Inventory2OutlinedIcon />
                )
              }
              onClick={onDownloadEvidencePack}
              disabled={isEvidenceBusy}
              sx={outlineSx}
            >
              {evidencePackDownloading
                ? "Downloading..."
                : "Download evidence pack"}
            </Button>
          )}

          {evidencePackStatus === "failed" && (
            <Button
              variant="outlined"
              size="medium"
              startIcon={<Inventory2OutlinedIcon />}
              onClick={onRequestEvidencePack}
              sx={{
                textTransform: "none",
                fontSize: "14px",
                fontWeight: 500,
                height: 38,
                px: 1.75,
                borderColor: "#B42318",
                color: "#B42318",
                "&:hover": {
                  borderColor: "#912018",
                  backgroundColor: "#FFF4F2",
                },
              }}
            >
              Retry evidence pack
            </Button>
          )}
        </>
      )}

      <Button
        variant="outlined"
        size="medium"
        startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
        onClick={onDownloadPdf}
        disabled={downloading}
        sx={outlineSx}
      >
        {downloading ? "Downloading..." : "Download PDF"}
      </Button>
    </Box>
  );
}
